import { Injectable } from '@angular/core';
import { Observable, delay, forkJoin, of } from 'rxjs';
import { EnvironmentInformationType } from 'src/app/models/environment-information-model';
import { NonHostedEnvDetail } from 'src/app/models/non-hosted-env-detail.model';
import { NonHostedEnvironment } from 'src/app/models/non-hosted-environment.model';
import { VmExpiryDatesResponse } from 'src/app/models/responses/vm-expiry-dates-response';
import { WebManagerPortResponse } from 'src/app/models/responses/web-manager-port-respones';
import { SingleEnvironmentDetails } from 'src/app/models/single-environment-details.model';
import { SingleMachineModel } from 'src/app/models/single-machine-model';
import { SingeServerCompleteDetails } from 'src/app/models/single-server-complete-details.model';
import { TwentyFourSupport } from 'src/app/models/twenty-four-support.model';
import { CompleteServerDetails } from 'src/app/interfaces/vm.interface';
import { EnvironmentDetail } from 'src/app/types/environment.type';
import { Region } from 'src/app/interfaces/plan.interface';

@Injectable({
  providedIn: 'root',
})
export class EnvironmentService {
  envTypes = ['prod', 'dev', 'dr'];
  regions = ['us-east-1', 'us-east-2', 'us-west-1', 'qatarcentral'];
  serverProfiles = ['Ent-8', 'Ent-4'];
  nCacheVersions = ['5.3', '5.4'];
  operatingSystems = ['Windows', 'Linux'];
  availabilityZones = ['a1', '2a', '1b', '2b'];

  monthlyPrice = {
    software: 20,
    infrastructure: 45,
    data: 10,
    support_24_7: 85,
  };
  yearlyPrice = {
    software: 120,
    infrastructure: 245,
    data: 110,
    support_24_7: 385,
  };

  twentyFourCheck: TwentyFourSupport = {
    enabled: true,
  };

  getSingleEnvironmentDetails(
    envName: string,
    subscriptionId
  ): Observable<SingleEnvironmentDetails> {
    const environment: SingleEnvironmentDetails = {
      EnvironmentName: 'Windows Environment-1',
      EnvironmentType: 'prod',
      LicenceType: 'Standard',
      VMSize: 't2.micro',
      NoOfServers: 3,
      OperatingSystem: 'Windows',
      CIDRBlock: '192.168.0.0/24',
      Region: 'us-east-1',
      PublicIP: '192.168.0.1',
      Subnet: '192.168.0.0/25',
    };
    return of(environment);
  }

  getSingleMachinePrice(
    envType: string,
    os: string,
    serverProfile: string,
    nCacheVersion: string,
    vendor: string,
    region: string
  ): Observable<SingleMachineModel> {
    const price: SingleMachineModel = {
      envType: envType,
      operatingSystem: os,
      serverProfile: serverProfile,
      nCacheVersion: nCacheVersion,
      vendor: vendor,
      region: region,
      yearlyPrice: this.yearlyPrice,
      monthlyPrice: this.monthlyPrice,
    };
    return of(price);
  }

  getAllRegions(): Observable<string[]> {
    return of(this.regions);
  }

  getAllEnvTypes(): Observable<string[]> {
    return of(this.envTypes);
  }

  getAllServerProfiles(): Observable<string[]> {
    return of(this.serverProfiles);
  }

  getAllOperatingSystems(): Observable<string[]> {
    return of(this.operatingSystems);
  }

  getAllNCacheVersions(): Observable<string[]> {
    return of(this.nCacheVersions);
  }

  getAllAvailabilityZones(): Observable<string[]> {
    return of(this.availabilityZones);
  }

  getServersByEnvironmentId(
    subscriptionId: string,
    environmentName: string
  ): Observable<CompleteServerDetails[]> {
    const servers: CompleteServerDetails[] = [
      {
        name: 'prod-app-server-01',
        type: 'production',
        publicIp: '192.0.2.1',
        privateIp: '10.0.0.1',
        vnet: '10.0.0.0/12',
        subnet: '10.0.0.0/24',
        region: 'us-east-1',
        os: 'Linux',
        profile: 't2.micro',
        resourceId: 'resource-1',
        expiration: '25 January 2025',
      },
      {
        name: 'dev-db-server-01',
        type: 'dev',
        publicIp: '203.0.113.2',
        privateIp: '10.0.0.2',
        vnet: '10.0.1.0/16',
        subnet: '10.0.1.0/24',
        region: 'eastus',
        os: 'Windows',
        profile: 'Standard_B1s',
        resourceId: 'resource-2',
        expiration: '25 January 2025',
      },
      {
        name: 'dr-backup-server-01',
        type: 'dr',
        publicIp: '198.51.100.3',
        privateIp: '10.0.0.3',
        vnet: '10.0.2.0/16',
        subnet: '10.0.2.0/24',
        region: 'eu-west-1',
        os: 'Linux',
        profile: 't3.medium',
        resourceId: 'resource-3',
        expiration: '25 January 2025',
      },
      {
        name: 'test-api-server-01',
        type: 'test',
        publicIp: '192.0.2.4',
        privateIp: '10.0.0.4',
        vnet: '10.0.3.0/16',
        subnet: '10.0.3.0/24',
        region: 'westus',
        os: 'Linux',
        profile: 'Standard_DS1_v2',
        resourceId: 'resource-4',
        expiration: '25 January 2025',
      },
      {
        name: 'prod-db-server-01',
        type: 'production',
        publicIp: '203.0.113.5',
        privateIp: '10.0.0.5',
        vnet: '10.0.4.0/16',
        subnet: '10.0.4.0/24',
        region: 'us-west-2',
        os: 'Windows',
        profile: 't3.large',
        resourceId: 'resource-5',
        expiration: '25 January 2025',
      },
      {
        name: 'dev-web-server-01',
        type: 'dev',
        publicIp: '198.51.100.6',
        privateIp: '10.0.0.6',
        vnet: '10.0.5.0/16',
        subnet: '10.0.5.0/24',
        region: 'centralus',
        os: 'Windows',
        profile: 'Standard_B2s',
        resourceId: 'resource-6',
        expiration: '25 January 2025',
      },
    ];

    return of(servers).pipe(delay(100));
  }

  ServerCompleteDetails: Array<SingeServerCompleteDetails> = [
    {
      InstanceName: 'Server 1',
      EnvironmentType: 'Dev',
      ExpiresOn: new Date('2024-12-28'),
      VMSize: 'DS2_V2',
      SubscriptionID: 'azure-subscription',
      Region: 'Us-east',
      OperatingSystem: 'Windows',
      ResourceGroup: 'resource-group2',
      VNet: 'test-vnet',
      Subnet: 'test-subnet',
      PublicIP: '10.0.0.12',
      PrivateIP: '40.02.02.2',
      CIDR: '10.0.0.0/24',
    },
    {
      InstanceName: 'server 2',
      EnvironmentType: 'Prod',
      ExpiresOn: new Date('2024-12-28'),
      VMSize: 't2.micro',
      SubscriptionID: 'azure-subscription',
      Region: 'Us-east',
      OperatingSystem: 'Windows',
      ResourceGroup: 'resource-group2',
      VNet: 'test-vnet',
      Subnet: 'test-subnet',
      PublicIP: '10.0.0.12',
      PrivateIP: '40.02.02.2',
      CIDR: '10.0.0.0/24',
    },
  ];
  getServerCompleteDetails(
    instanceName: string,
    publicIP: string
  ): Observable<SingeServerCompleteDetails | undefined> {
    return of(
      this.ServerCompleteDetails.find(
        (server) =>
          server.InstanceName.toLocaleLowerCase() ===
            instanceName.toLocaleLowerCase() && server.PublicIP === publicIP
      )
    );
  }

  getAllInformation(): Observable<EnvironmentInformationType> {
    return forkJoin({
      envTypes: this.getAllEnvTypes(),
      regions: [],
      serverProfiles: this.getAllServerProfiles(),
      nCacheVersions: this.getAllNCacheVersions(),
      operatingSystems: this.getAllOperatingSystems(),
      availabilityZones: this.getAllAvailabilityZones(),
    });
  }

  getCoupon(
    couponId: string,
    subscriptionId: string
  ): Observable<SingleMachineModel[]> {
    return of([]);
  }

  support24x7Enabled(): Observable<TwentyFourSupport> {
    return of(this.twentyFourCheck);
  }

  getVmExpiryDates(
    cloudSubscriptionId: string
  ): Observable<VmExpiryDatesResponse> {
    let date: VmExpiryDatesResponse = {
      vmMonthlyExpiryDate: Date.now().toString(),
      vmYearlyExpiryDate: Date.now().toString(),
    };
    return of(date);
    // return this.http.get(environment.api.routes.environment.get_vm_expiry_dates, { CloudSubscriptionId: cloudSubscriptionId });
  }
  getNonHostedEnvironmentDetail(
    subscriptionId: string,
    envName: string
  ): Observable<NonHostedEnvDetail> {
    // var url = Helper.formatString(environment.api.routes.environment.get_non_hosted_environment, subscriptionId, envName);
    // return this.http.get<NonHostedEnvDetail>(url);

    // Create a mock NonHostedEnvironment object
    const nonHostedEnvironment: NonHostedEnvironment = {
      id: 'env-001', // Unique identifier for the environment
      name: 'Development Environment', // Name of the environment
      type: 'Non-Hosted', // Type of the environment
      serverLicenses: 10, // Total number of server licenses available
      serverLicensesAvailable: 5, // Number of available server licenses
      clientLicenses: 20, // Total number of client licenses available
      clientLicensesAvailable: 15, // Number of available client licenses
      licenseType: 'Standard', // Type of license (e.g., Standard, Premium)
    };

    // Create an instance of NonHostedEnvDetail
    const nonHostedEnvDetail: NonHostedEnvDetail = {
      environmentInfo: nonHostedEnvironment,
      serverInfo: [], // This can be populated with ActivationHistory objects
    };
    return of(nonHostedEnvDetail);
  }

  getWebManagerPortByEnv(
    subscriptionId: string,
    environmentName: string
  ): Observable<WebManagerPortResponse> {
    // return this.http.get(environment.api.routes.environment.get_web_manager_port, { subscriptionId: subscriptionId, environmentName: environmentName })
    const port: WebManagerPortResponse = {
      webManagerPort: '9800',
    };
    return of(port);
  }

  deleteEnvironment(
    EnvironmentName: string,
    SubscriptionId: string
  ): Observable<string> {
    // return this.http.post(environment.api.routes.environment.delete, { SubscriptionId, EnvironmentName })
    return of(null);
  }

  restoreEnvironment(
    EnvironmentName: string,
    SubscriptionId: string
  ): Observable<void> {
    // return this.http.post(environment.api.routes.environment.restore, { SubscriptionId, EnvironmentName })
    return of(null);
  }

  getDecriptedPassword(
    instanceId: string,
    privateKey: string
  ): Observable<any> {
    const body = { PrivateKey: privateKey, InstanceId: instanceId };
    // return this.http.post(environment.api.routes.environment.get_ec2_password, body);
    return of(null);
  }

  environmentDetail: EnvironmentDetail = {
    environmentType: 'Production',
    environmentName: 'abc',
    cidrBlock: '10.0.0.0/16',
    edition: 'Enterprise',
    subNet: '10.0.1.0/24',
    ncacheVersion: 4.6,
    region: 'us-west-2',
    cloudProvider: 'AWS',
    serverProfile: 'ENT-4 (4 GB): 2 vCPUs',
    reservedCapacity: 100,
    servers: [
  
    ],
    vpcs: [
      {
        peeringName: 'VPCPeering1',
        ownerId: '123456789012',
        cidr: '10.0.2.0/24',
        vpcId: 'vpc-0123456789abcdef',
      },
      {
        peeringName: 'VPCPeering2',
        ownerId: '123456789013',
        cidr: '10.0.3.0/24',
        vpcId: 'vpc-abcdef1234567890',
      },
    ],
    privateLinks: [
      {
        linkName: 'PrivateLink1',
        privateLinkId: 'pl-0123456789abcdef',
        cidr: '10.0.4.0/24',
        ownerId: '123456789012',
      },
      {
        linkName: 'PrivateLink2',
        privateLinkId: 'pl-abcdef1234567890',
        cidr: '10.0.5.0/24',
        ownerId: '123456789013',
      },
    ],
  };

  getEnvironmentDetails(envId: number) {
    return this.environmentDetail;
  }
}
